import type { Component } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { constant } from '@/constant';
import type { Module, ProjectModuleEventItemConfigDto, ProjectModuleNewsItemConfigDto } from '@/store/module.interface';

type BlockTypes = typeof constant.Block.Type;
export type BlockType = BlockTypes[keyof BlockTypes];

// The types each host draws: a page its own four and the shared ones, a project every type but a page's own four.
// Mirrors `Block.Hosts` in api/src/constant/index.js, which the client does not carry, so that a renderer registered
// on the wrong host fails to compile instead of drawing a block nobody can place there.
type PageOnlyBlockTypeName = 'FeaturedProjects' | 'HomepageItems' | 'LinksCarousel' | 'HomepageHero';
type SharedBlockTypeName = 'Hero';
export type HostBlockType = {
  project: BlockTypes[Exclude<keyof BlockTypes, PageOnlyBlockTypeName>];
  page: BlockTypes[PageOnlyBlockTypeName | SharedBlockTypeName];
};

// The kinds of parent a block sits on. Each one has its own layout and views, so a theme registers per host.
export type ThemeHost = 'project' | 'page';

export type DesignSystem = 'legacy' | 'modern';

export type ComponentLoader = () => Promise<Component>;

// A follow-on page of a host, opened from one of its blocks: a news item's detail, an event's.
export const expansionPageKeys = {
  NewsItem: 'news-item',
  EventItem: 'event-item',
} as const;

export type ExpansionPageKey = (typeof expansionPageKeys)[keyof typeof expansionPageKeys];

export type ProjectExpansionPageItem = ProjectModuleNewsItemConfigDto | ProjectModuleEventItemConfigDto;

export interface ProjectExpansionPagePayload {
  key: ExpansionPageKey;
  module: Module | null;
  itemId: number | null;
  item: ProjectExpansionPageItem | null;
  backTo: RouteLocationRaw;
}

// What a theme draws for one host.
export interface HostTheme<H extends ThemeHost = ThemeHost> {
  designSystem: DesignSystem;
  // The default layout: a project's only one, a page's when its `layout_key` is null or names no layout in this theme
  // or its parent.
  layout: ComponentLoader;
  // Named alternatives, by the key a page stores in `layout_key`, offered by the theme manifest.
  layouts?: Record<string, ComponentLoader>;
  blockRenderers: Partial<Record<HostBlockType[H], ComponentLoader>>;
  expansionPageRenderers?: Partial<Record<ExpansionPageKey, ComponentLoader>>;
}

export interface ThemeRegistration {
  // A base theme, whose components this one uses where it registers none. One level only: a parent has no parent.
  parent?: string;
  project?: HostTheme<'project'>;
  page?: HostTheme<'page'>;
}
